import classNames from 'classnames'
import { useGetPartnerData } from 'legacy/lut/features/partner/hooks/useGetPartnerData'
import { getUrlPartnerId } from 'legacy/lut/helpers/getUrlPartnerId'
import { FC, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { RequestStatus } from 'clickNgo/configs/service/common'
import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Loader } from 'core-components/library/loader'
import { TabButtons } from 'core-components/library/tabButtons'

import { AllContactsTab } from './components/tabs/allContactsTab/AllContactsTab'
import { NewContactTab } from './components/tabs/newContactTab/NewContactTab'
import { ProfilingTeamRequestTab } from './components/tabs/profilingTeamRequestTab/ProfilingTeamRequestTab'
import { TABS_CONFIG } from './configs'
import { isSelectedPartnerEqual } from './helpers/isSelectedPartnerEqual'
import { IEditContactParams, IWidgetPartnerContactsProps } from './models'
import { selectBusinessProfileState } from './selectors/selectBusinessProfileState'

import styles from './PartnerContacts.module.scss'

export const WidgetPartnerContacts: FC<IWidgetPartnerContactsProps> = ({
  appSource,
  className,
  flexWidth,
  onSelectProfile,
}) => {
  const { selectedPartner } = useSelector(selectBusinessProfileState, isSelectedPartnerEqual)
  const partnerId = getUrlPartnerId() || selectedPartner
  const { partnerData, fetchPartnerData, status } = useGetPartnerData(partnerId)
  const hasMissedRoles = Boolean(partnerData?.missedRoles.length)
  const isPartnerDataLoading = status === RequestStatus.PENDING

  const warningMissedRolesMessage = useMemo(
    () => (
      <>
        <Translation text="BUSINESS_PROFILE_MISSING_KEY_ROLES" span />
        {`: ${partnerData?.missedRoles.join(', ')}`}
      </>
    ),
    [partnerData?.missedRoles]
  )

  const onEditContactHandler = useCallback(
    (params?: IEditContactParams) => {
      if (params && params.isUpdated) {
        fetchPartnerData()
      }
    },
    [fetchPartnerData]
  )

  if (!partnerId || !partnerData) {
    return null
  }

  if (isPartnerDataLoading) {
    return <Loader />
  }

  return (
    <div className={classNames(styles.root, className)}>
      {/** HEADER INFO / WARNING / ERROR BLOCK */}
      {hasMissedRoles && <InfoBlock text={warningMissedRolesMessage} type={InfoBlockType.Danger} />}
      <TabButtons
        urlKey=""
        className="cmp-BusinessProfile-TabButtons"
        items={[
          {
            /** All contacts tab */
            id: TABS_CONFIG.allContacts.id,
            label: TABS_CONFIG.allContacts.label,
            content: () => (
              <AllContactsTab
                partnerId={partnerId}
                flexWidth={flexWidth}
                onSelectProfile={onSelectProfile}
                appSource={appSource}
                onEditContact={onEditContactHandler}
              />
            ),
          },
          {
            /** Profiling team requests tab */
            id: TABS_CONFIG.profilingTeamRequests.id,
            label: TABS_CONFIG.profilingTeamRequests.label,
            content: () => (
              <ProfilingTeamRequestTab
                partnerId={partnerId}
                flexWidth={flexWidth}
                onSelectProfile={onSelectProfile}
                appSource={appSource}
                onEditContact={onEditContactHandler}
              />
            ),
          },
          {
            /** New contacts tab */
            id: TABS_CONFIG.newContacts.id,
            label: TABS_CONFIG.newContacts.label,
            content: () => (
              <NewContactTab
                partnerId={partnerId}
                flexWidth={flexWidth}
                onSelectProfile={onSelectProfile}
                appSource={appSource}
                onEditContact={onEditContactHandler}
              />
            ),
          },
        ]}
      />
    </div>
  )
}

export const getMetaInfo = () => {
  return {
    urlMapping: [{ propName: 'pid', isRequired: true }],
  }
}

export default WidgetPartnerContacts
